import { Component, inject, input, output } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { ControlsOf, FormGroup } from '@ngneat/reactive-forms';
import { Realisation } from 'src/app/classes/realisation';
import { CodaltComponent } from 'src/app/codalt.component';
import type { FgRealisation } from 'src/app/components/hour-control/hour-control.types';
import { GenericModule } from 'src/app/generic/generic.module';
import { CommentDialogComponent } from 'src/app/pages/hour-registration/comment-dialog/comment-dialog.component';
import { HistoryDialogComponent } from 'src/app/pages/hour-registration/history-dialog/history-dialog.component';
import { AuthorisationService } from 'src/app/services/auth/authorisation.service';
import { Functionalities } from 'src/app/services/auth/functionalities';

@Component({
    selector: 'app-realisation-row-buttons',
    imports: [GenericModule, MatButtonModule, MatIconModule],
    templateUrl: './realisation-row-buttons.component.html',
    styleUrl: './realisation-row-buttons.component.scss',
})
export class RealisationRowButtonsComponent extends CodaltComponent {
    readonly form = input.required<FormGroup<ControlsOf<FgRealisation>>>();
    readonly notApprovable = input(false);
    readonly notEditable = input(false);
    readonly realisation = input.required<Realisation>();

    readonly approveRequested = output<void>();
    readonly restoreRequested = output<void>();
    readonly removeRequested = output<void>();

    readonly canApproveHours = AuthorisationService.canApproveHours();
    readonly Functionalities = Functionalities;

    private readonly dialog = inject(MatDialog);

    approve(event: Event): void {
        event.preventDefault();
        event.stopPropagation();

        if (this.canApproveHours) {
            this.approveRequested.emit();
        }
    }

    skipPerformer(event: Event, onConfirm?: () => void): void {
        this.editComment(event, 'comment_manager', true, () => {
            this.form().controls.skip_performer.setValue(true);
            onConfirm?.();
        });
    }

    restore(event: Event): void {
        event.preventDefault();
        event.stopPropagation();
        this.restoreRequested.emit();
    }

    remove(event: Event): void {
        event.preventDefault();
        event.stopPropagation();

        const removeRealisation = () => this.removeRequested.emit();

        if (
            !AuthorisationService.hasFunctionality(Functionalities.HourTimeEdit) &&
            !AuthorisationService.hasFunctionality(Functionalities.CommonHourCheck)
        ) {
            this.skipPerformer(event, removeRealisation);
        } else {
            removeRealisation();
        }
    }

    editComment(
        event: Event,
        field: 'comment_user' | 'comment_manager',
        required = false,
        onConfirm?: () => void
    ): void {
        event.preventDefault();
        event.stopPropagation();

        const realisation = this.realisation();
        const form = this.form();
        const commentControl = form.controls[field];
        const ref = this.dialog.open(CommentDialogComponent, {
            maxWidth: '500px',
            maxHeight: '100%',
            disableClose: false,
            panelClass: 'comment-edit-dialog',
            data: {
                comment: commentControl.value,
                required,
            },
        });

        this.subscriptions.add(
            ref.afterClosed().subscribe(comment => {
                if (comment !== undefined && (!required || comment)) {
                    onConfirm?.();
                    commentControl.setValue(comment);
                    realisation[field] = commentControl.value;
                    form.markAsDirty();
                }
            })
        );
    }

    showHistory(event: Event): void {
        event.preventDefault();
        event.stopPropagation();

        this.dialog.open(HistoryDialogComponent, {
            maxWidth: '99vw',
            width: '1600px',
            maxHeight: '100%',
            disableClose: false,
            panelClass: 'comment-edit-dialog',
            data: {
                realisation: this.realisation(),
            },
        });
    }
}
